import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { OneTrustConsentGroups } from '~/enums';
import {
  getConsentedGroupsFromCookie,
  USER_OPTED_IN_EVENT_NAME,
} from '~/helpers/consent.helper';
import { isInIframe } from '~/iframeMode';

// null until OneTrust has reported, so a script that has not loaded yet is not read as a rejection
const readActiveGroupsFromOneTrust = (): string[] | null => {
  const activeGroups = window.OnetrustActiveGroups;
  if (!activeGroups) return null;

  return activeGroups.split(',').filter((group) => group.trim().length > 0);
};

const useConsent = () => {
  const postHog = usePostHog();
  const [consentData, setConsentData] = useState<string[] | null>(
    () => readActiveGroupsFromOneTrust() ?? getConsentedGroupsFromCookie()
  );

  const handleEventCapturing = (userAcceptedTracking: boolean) => {
    const alreadyOptedIn = postHog.has_opted_in_capturing();

    if (userAcceptedTracking && !alreadyOptedIn) {
      postHog.opt_in_capturing({
        captureEventName: USER_OPTED_IN_EVENT_NAME,
      });
    } else if (!userAcceptedTracking && alreadyOptedIn) {
      postHog.opt_out_capturing();
    }
  };

  const handleSessionRecording = (userAcceptedTracking: boolean) => {
    const recordingAlreadyStarted = postHog.sessionRecordingStarted();

    if (userAcceptedTracking && !recordingAlreadyStarted) {
      postHog.startSessionRecording();
    } else if (!userAcceptedTracking && recordingAlreadyStarted) {
      postHog.stopSessionRecording();
    }
  };

  useEffect(() => {
    const readDataAndSetConsentPref = (event: Event) => {
      if (window.OneTrust && event instanceof CustomEvent) {
        setConsentData(event.detail);
      }
    };

    window.addEventListener('consent.onetrust', readDataAndSetConsentPref);

    return () => {
      window.removeEventListener('consent.onetrust', readDataAndSetConsentPref);
    };
  }, []);

  useEffect(() => {
    if (!postHog || consentData === null) return;

    if (import.meta.env.VITE_POSTHOG_TRACKING_REQUIRES_CONSENT === 'true') {
      const userAcceptedTracking = consentData.includes(
        OneTrustConsentGroups.PerformanceCookies
      );

      postHog.setPersonProperties({
        opted_out_tracking: !userAcceptedTracking,
      });

      if (import.meta.env.VITE_POSTHOG_EVENT_CAPTURING_ENABLED === 'true') {
        handleEventCapturing(userAcceptedTracking);
      }

      if (
        import.meta.env.VITE_POSTHOG_SESSION_RECORDING_ENABLED === 'true' &&
        !isInIframe
      ) {
        handleSessionRecording(userAcceptedTracking);
      }
    }
  }, [consentData, postHog, handleEventCapturing, handleSessionRecording]);
};

export default useConsent;
